import type {
  RegisterInput,
  Configuration,
  CreateUserInput,
  LinkFreeProtectionBillingRequest,
  UpdateUserInput,
  ListInitiatorsResponse,
  ListUsersResponse,
  UserType,
} from '@repo/api-gw-sdk';
import {
  CreateUserApi,
  DeleteUserApi,
  ListInitiatorsApi,
  ListUsersApi,
  RegisterApi,
  UpdateUserOverrideApi,
  UpdateViewerRoleApi,
  RefreshApi,
  GetViewerApi,
  AcceptMsaApi,
  LogoutApi,
  UpdateAccountProfileApi,
} from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/users';

export const users = (
  httpClient: HttpClient,
  config: Configuration,
  isDevOrTest: boolean
) => {
  const refreshApi = new RefreshApi(config);
  const registerApi = new RegisterApi(config);
  const listUsersApi = new ListUsersApi(config);
  const listInitiatorsApi = new ListInitiatorsApi(config);
  const deleteUserApi = new DeleteUserApi(config);
  const createUserApi = new CreateUserApi(config);
  const updateUserApi = new UpdateUserOverrideApi(config);
  const updateViewerRoleApi = new UpdateViewerRoleApi(config);
  const getViewerApi = new GetViewerApi(config);
  const acceptMsaApi = new AcceptMsaApi(config);
  const logoutApi = new LogoutApi(config);
  const updateAccountProfileApi = new UpdateAccountProfileApi(config);

  return {
    // Omitting userType returns every user of the account, SAML-provisioned ones included.
    // `enabled: false` skips the request, for callers that only need the list conditionally.
    useList: (userType?: UserType, enabled = true) =>
      httpClient.execute(
        (type?: UserType) =>
          listUsersApi.listUsers(type).catch((err) => {
            if (isDevOrTest) {
              return {
                users: [],
                pagination: {
                  recordsCount: 0,
                },
              } satisfies ListUsersResponse;
            }

            throw err;
          }),
        enabled ? baseUrl : null,
        '',
        userType
      ),
    create: (user: CreateUserInput) => createUserApi.createUser(user),
    update: (id: string, user: UpdateUserInput) =>
      updateUserApi.updateUserOverride(id, user),
    delete: (id: string) => deleteUserApi.deleteUser(id),
    register: (registerInput: RegisterInput) =>
      registerApi.register(registerInput),
    changeRole: (roleId: string) =>
      updateViewerRoleApi.updateViewerRole({ roleId: roleId }),
    refreshToken: (redirectUrl?: string) => refreshApi.refresh(redirectUrl),
    useViewer: (options?: { refreshInterval: number }) =>
      httpClient.executeWithConfig(
        getViewerApi.getViewer.bind(getViewerApi),
        baseUrl,
        '/viewer',
        options
      ),
    // Users and api credentials merged into one slim list, for rendering an initiator by ID
    // without pulling the full user / api-credential entities.
    initiators: {
      useList: () =>
        httpClient.execute(
          (...args: Parameters<typeof listInitiatorsApi.listInitiators>) =>
            listInitiatorsApi.listInitiators(...args).catch((err) => {
              if (isDevOrTest) {
                return { initiators: [] } satisfies ListInitiatorsResponse;
              }

              throw err;
            }),
          baseUrl,
          '/initiators'
        ),
    },
    acceptMsa: () => acceptMsaApi.acceptMsa(),
    linkFreeProtectionBilling: (request: LinkFreeProtectionBillingRequest) =>
      updateAccountProfileApi.linkFreeProtectionBilling(request),
    // Expires every session cookie server-side; the caller then redirects to
    // /login. The response body is unused.
    logout: () => logoutApi.logout(),
  };
};
